import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  checkTeacherAcademySkill,
  AcademyAccessDeniedError,
  type AcademyConfirmedBy,
  type AcademyLesson,
  type AcademySkillCheckResult,
  type AcademySkillPart,
} from '../../../pages/academy/Academy/AcademyService';
import { AppliedActionsSummary } from '../AppliedActionsSummary/AppliedActionsSummary';
import { LearningContentDetails } from '../LearningContentDetails/LearningContentDetails';
import { LearningContentStage } from '../LearningContentStage/LearningContentStage';
import { LessonStep } from '../LessonStep/LessonStep';
import { PhaseCompleteStatus } from '../PhaseCompleteStatus/PhaseCompleteStatus';
import {
  SkillCheckNotice,
  type SkillCheckNoticeState,
} from '../SkillCheckNotice/SkillCheckNotice';
import {
  getAcademyMaskedIconProps,
} from '../Utilities/academyMaskedIcon';
import thumbUpAltOutlinedIconUrl from './Images/thumb-up-alt-outlined.svg';
import { skillDetailClassNames, skillDetailStyles } from './SkillDetail.styles';

export interface SkillProgressUpdate {
  learnConfirmedBy?: AcademyConfirmedBy;
  applyConfirmedBy?: AcademyConfirmedBy;
  learnCompletedAt?: string;
  applyCompletedAt?: string;
}

export function SkillDetail({
  skill,
  planToken,
  onProgressUpdate,
}: {
  skill: AcademyLesson;
  planToken: string;
  onProgressUpdate: (skillKey: string, update: SkillProgressUpdate) => void | Promise<void>;
}) {
  const theme = useTheme();
  const [learnCompletedAt, setLearnCompletedAt] = useState<string | null>(
    skill.learnCompletedAt || null,
  );
  const [applyCompletedAt, setApplyCompletedAt] = useState<string | null>(
    skill.applyCompletedAt || null,
  );
  const [isLearnConfirmed, setIsLearnConfirmed] = useState(skill.learnConfirmedBy !== 'none');
  const [isApplyConfirmed, setIsApplyConfirmed] = useState(skill.applyConfirmedBy !== 'none');
  const [skillCheckNotice, setSkillCheckNotice] = useState<SkillCheckNoticeState | null>(null);
  const pendingCheckPartRef = useRef<AcademySkillPart | null>(null);
  const confirmedPartsRef = useRef({
    learn: skill.learnConfirmedBy !== 'none',
    apply: skill.applyConfirmedBy !== 'none',
  });
  const [pendingCheckPart, setPendingCheckPart] = useState<AcademySkillPart | null>(null);
  const learnCheckNotice = getSkillCheckNotice('learn', skillCheckNotice);
  const applyCheckNotice = getSkillCheckNotice('apply', skillCheckNotice);
  const isLearnActionBlocked = Boolean(learnCheckNotice && learnCheckNotice.status !== 'error');
  const isApplyActionReplaced = Boolean(applyCheckNotice && applyCheckNotice.status !== 'error');
  const shouldShowApplyIncompleteNotice = isApplyConfirmed && applyCheckNotice?.status === 'teacher_allowed';
  const shouldShowApplyStep = isLearnConfirmed && skill.applyRequired;

  useEffect(() => {
    const isNextLearnConfirmed = skill.learnConfirmedBy !== 'none';
    const isNextApplyConfirmed = skill.applyConfirmedBy !== 'none';

    setLearnCompletedAt(skill.learnCompletedAt || null);
    setApplyCompletedAt(skill.applyCompletedAt || null);
    setIsLearnConfirmed(isNextLearnConfirmed);
    setIsApplyConfirmed(isNextApplyConfirmed);
    confirmedPartsRef.current = {
      learn: isNextLearnConfirmed,
      apply: isNextApplyConfirmed,
    };
  }, [
    skill.skillKey,
    skill.learnConfirmedBy,
    skill.applyConfirmedBy,
    skill.learnCompletedAt,
    skill.applyCompletedAt,
  ]);

  async function handleLearnComplete() {
    await handleSkillPartCheck('learn');
  }

  async function handleApplyConfirm() {
    await handleSkillPartCheck('apply');
  }

  async function handleLearnConfirmAnyway() {
    await handleSkillPartCheck('learn', 'self');
  }

  async function handleApplyConfirmAnyway() {
    await handleSkillPartCheck('apply', 'self');
  }

  async function handleSkillPartCheck(
    part: AcademySkillPart,
    confirmedBy?: 'self',
  ) {
    const currentNotice = getSkillCheckNotice(part, skillCheckNotice);

    if (
      pendingCheckPartRef.current ||
      pendingCheckPart ||
      confirmedPartsRef.current[part]
    ) {
      return;
    }

    pendingCheckPartRef.current = part;
    setPendingCheckPart(part);
    setSkillCheckNotice(null);

    try {
      const result = await runSkillCheck(part, confirmedBy);

      if (!result || result.confirmedBy === 'none') {
        return;
      }

      const incompleteOverrideNotice = getIncompleteOverrideNotice(part, result, currentNotice);

      if (incompleteOverrideNotice) {
        setSkillCheckNotice(incompleteOverrideNotice);
      }

      const completedAt = getCurrentCompletionDate();
      markPartConfirmed(part, completedAt);

      await onProgressUpdate(
        skill.skillKey,
        part === 'learn'
          ? {
              learnConfirmedBy: result.confirmedBy,
              learnCompletedAt: completedAt,
            }
          : {
              applyConfirmedBy: result.confirmedBy,
              applyCompletedAt: completedAt,
            },
      );
    } finally {
      pendingCheckPartRef.current = null;
      setPendingCheckPart(null);
    }
  }

  function markPartConfirmed(part: AcademySkillPart, completedAt: string) {
    confirmedPartsRef.current = {
      ...confirmedPartsRef.current,
      [part]: true,
    };

    if (part === 'learn') {
      setIsLearnConfirmed(true);
      setLearnCompletedAt(completedAt);
      return;
    }

    setIsApplyConfirmed(true);
    setApplyCompletedAt(completedAt);
  }

  async function runSkillCheck(
    part: AcademySkillPart,
    confirmedBy?: 'self',
  ): Promise<AcademySkillCheckResult | null> {
    try {
      const result = await checkTeacherAcademySkill(skill.skillKey, {
        planToken,
        part,
        confirmedBy,
      });

      const warnings = result.checkItems.filter((item) => !item.passed);

      if (result.confirmedBy === 'none' && warnings.length > 0) {
        setSkillCheckNotice({
          part,
          status: result.teacherConfirmationAllowed ? 'teacher_allowed' : 'teacher_blocked',
          messages: warnings.map((item) => item.label),
        });
      }

      return result;
    } catch (error: unknown) {
      if (error instanceof AcademyAccessDeniedError) {
        window.location.assign('/experience');
        return null;
      }

      setSkillCheckNotice({
        part,
        status: 'error',
        messages: [getSkillCheckErrorMessage(error)],
      });
      return null;
    }
  }

  return (
    <Box className={skillDetailClassNames.page} sx={skillDetailStyles.page()}>
      <Box
        component="section"
        className={skillDetailClassNames.pageRoot}
        sx={skillDetailStyles.pageRoot()}
        aria-label={skill.title}
      >
        <Box className={skillDetailClassNames.body} sx={skillDetailStyles.body()}>
          <Box className={skillDetailClassNames.stepList} sx={skillDetailStyles.stepList(theme)}>
            <LessonStep
              stepNumber={1}
              title={getLearningStepHeading(skill)}
              status={
                isLearnConfirmed ? <PhaseCompleteStatus completedAt={learnCompletedAt} /> : null
              }
            >
              <Box
                className={skillDetailClassNames.learningContent}
                sx={skillDetailStyles.learningContent()}
              >
                <LearningContentStage
                  skill={skill}
                  isComplete={isLearnConfirmed}
                  isChecking={pendingCheckPart === 'learn' || isLearnActionBlocked}
                  onComplete={handleLearnComplete}
                />
              </Box>
              <SkillCheckNotice
                notice={learnCheckNotice}
                part="learn"
                isChecking={pendingCheckPart === 'learn'}
                showRefresh={!isLearnConfirmed}
                showConfirmAnyway={!isLearnConfirmed}
                onRefresh={handleLearnComplete}
                onConfirmAnyway={handleLearnConfirmAnyway}
              />
              <LearningContentDetails skill={skill} />
            </LessonStep>

            {shouldShowApplyStep ? (
              <LessonStep
                stepNumber={2}
                title={getApplyPrompt(skill)}
                status={
                  isApplyConfirmed ? <PhaseCompleteStatus completedAt={applyCompletedAt} /> : null
                }
              >
                <Box
                  className={skillDetailClassNames.applyPhase}
                  sx={skillDetailStyles.applyPhase()}
                >
                  {shouldShowApplyIncompleteNotice ? (
                    <SkillCheckNotice
                      notice={applyCheckNotice}
                      part="apply"
                      isChecking={pendingCheckPart === 'apply'}
                      showRefresh={false}
                      showConfirmAnyway={false}
                      onRefresh={handleApplyConfirm}
                      onConfirmAnyway={handleApplyConfirmAnyway}
                    />
                  ) : isApplyConfirmed ? (
                    <AppliedActionsSummary skill={skill} />
                  ) : (
                    <>
                      <SkillCheckNotice
                        notice={applyCheckNotice}
                        part="apply"
                        isChecking={pendingCheckPart === 'apply'}
                        showRefresh
                        showConfirmAnyway
                        onRefresh={handleApplyConfirm}
                        onConfirmAnyway={handleApplyConfirmAnyway}
                      />
                      {isApplyActionReplaced ? null : (
                        <Button
                          type="button"
                          className={skillDetailClassNames.applyButton}
                          sx={skillDetailStyles.applyButton()}
                          disableRipple
                          disabled={pendingCheckPart === 'apply'}
                          onClick={handleApplyConfirm}
                          startIcon={<ThumbsUpIcon />}
                        >
                          Mark as Applied
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </LessonStep>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function getSkillCheckNotice(
  part: AcademySkillPart,
  skillCheckNotice: SkillCheckNoticeState | null,
): SkillCheckNoticeState | null {
  return skillCheckNotice?.part === part ? skillCheckNotice : null;
}

function getIncompleteOverrideNotice(
  part: AcademySkillPart,
  result: AcademySkillCheckResult,
  currentNotice: SkillCheckNoticeState | null,
): SkillCheckNoticeState | null {
  if (result.confirmedBy !== 'self' || currentNotice?.status !== 'teacher_allowed') {
    return null;
  }

  const warningMessages = result.checkItems
    .filter((item) => !item.passed)
    .map((item) => item.label);

  return {
    part,
    status: 'teacher_allowed',
    messages: warningMessages.length > 0 ? warningMessages : currentNotice.messages,
  };
}

function getApplyPrompt(skill: AcademyLesson): string {
  if (skill.selfReportPrompt) {
    return skill.selfReportPrompt;
  }

  return skill.description || 'Confirm this step when the classroom action is complete.';
}

function getCurrentCompletionDate(): string {
  return new Date().toISOString();
}

function isVideoContent(contentType: AcademyLesson['learnContent']['contentType']): boolean {
  return contentType === 'video' || contentType === 'interactive_video';
}

function getLearningStepHeading(skill: AcademyLesson): string {
  return skill.learnContent.details.intro || getLearningStepTitle(skill);
}

function getLearningStepTitle(skill: AcademyLesson): string {
  return `${getLearningContentActionLabel(skill.learnContent.contentType)}: ${
    skill.learnContent.title
  }`;
}

function getLearningContentActionLabel(
  contentType: AcademyLesson['learnContent']['contentType'],
): string {
  if (isVideoContent(contentType)) {
    return 'Watch';
  }

  if (contentType === 'quick_tour') {
    return 'Take a tour';
  }

  return 'Read';
}

function getSkillCheckErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message === 'Academy is not available for this user.') {
    return error.message;
  }

  return 'Academy could not check this skill. Please try again.';
}

function ThumbsUpIcon() {
  return <Box component="span" {...getAcademyMaskedIconProps(thumbUpAltOutlinedIconUrl)} />;
}
